import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Zap, ChevronLeft, Users, Loader2, Send, X, Star, Clock, UserPlus, Check } from "lucide-react";

function fmt_secs(s) {
  if (s == null || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function UflQuickPlay() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ufl/state");
      setState(data);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Poll while in queue or in an open room
  useEffect(() => {
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load]);

  const join = async (uflUsername) => {
    setBusy(true);
    try {
      await api.post("/ufl/queue", { ufl_username: (uflUsername || "").trim() || null });
      toast.success(t("ufl.qp.toast.queued"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const leave = async () => {
    setBusy(true);
    try {
      await api.delete("/ufl/queue");
      toast(t("ufl.qp.toast.leftQueue"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <Loader2 className="w-6 h-6 animate-spin text-[#60A5FA]" />
      </div>
    );
  }
  const room = state.active_room;

  return (
    <div className="relative min-h-screen bg-[#020617] text-white pb-24 overflow-hidden" data-testid="v2-ufl-qp">
      {/* Ambient pitch aurora */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="ufl-aurora absolute -top-24 -left-16 w-72 h-72 rounded-full bg-[#10B981]/20 blur-3xl" />
        <div className="ufl-aurora absolute top-40 -right-20 w-80 h-80 rounded-full bg-[#1E3A8A]/25 blur-3xl" style={{ animationDelay: "-4s" }} />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 50% 0,#fff 1px,transparent 1px)", backgroundSize: "22px 22px" }} />
      </div>

      <div className="relative px-4 pt-5 pb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981] text-[#020617] text-[9px] font-extrabold uppercase tracking-[0.3em] shadow-[0_0_18px_rgba(16,185,129,0.5)]">
          <Zap className="w-3 h-3" /> Quick Play
        </span>
        <button
          type="button"
          onClick={() => navigate("/v2/ufl/mobile")}
          data-testid="v2-ufl-qp-back"
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> {t("ufl.hub.back")}
        </button>
      </div>

      <div className="relative px-4 space-y-4">
        {!state.in_queue && !room && (
          <IdleCard t={t} state={state} onJoin={join} busy={busy} />
        )}
        {state.in_queue && !room && (
          <QueuingCard t={t} state={state} onLeave={leave} busy={busy} />
        )}
        {room && (
          <RoomView t={t} room={room} userId={user?.id} onReload={load} />
        )}
      </div>
    </div>
  );
}

function IdleCard({ t, state, onJoin, busy }) {
  const qsize = state.queue_size || 0;
  const [uflName, setUflName] = useState(state.my_ufl_username || "");
  return (
    <div className="ufl-pop relative rounded-3xl bg-gradient-to-br from-[#10B981]/15 to-[#0F172A] border border-[#10B981]/40 p-6 text-center overflow-hidden" data-testid="v2-ufl-qp-idle">
      {/* Bolt with sonar rings */}
      <div className="relative mx-auto mb-3 w-16 h-16 flex items-center justify-center">
        <span className="ufl-sonar absolute inset-0 rounded-full border border-[#10B981]/50" />
        <span className="ufl-sonar absolute inset-0 rounded-full border border-[#10B981]/40" style={{ animationDelay: "0.8s" }} />
        <span className="ufl-sonar absolute inset-0 rounded-full border border-[#10B981]/30" style={{ animationDelay: "1.6s" }} />
        <Zap className="ufl-bolt relative w-12 h-12 text-[#10B981]" />
      </div>
      <h2 className="font-heading text-xl uppercase tracking-wider">{t("ufl.qp.idle.title")}</h2>
      <p className="text-[12px] text-white/70 mt-2 max-w-sm mx-auto">{t("ufl.qp.idle.body")}</p>

      {/* 4v4 formation visual */}
      <div className="mt-5 flex items-center justify-center gap-3" aria-hidden="true">
        <div className="flex gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <span key={`a${i}`} className="ufl-pop w-7 h-7 rounded-full bg-[#10B981]/20 border border-[#10B981]/50 flex items-center justify-center text-[13px]" style={{ animationDelay: `${i * 0.1}s` }}>⚽</span>
          ))}
        </div>
        <span className="text-[11px] font-heading font-extrabold text-white/50 tracking-widest">4v4</span>
        <div className="flex gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <span key={`b${i}`} className="ufl-pop w-7 h-7 rounded-full bg-[#60A5FA]/20 border border-[#60A5FA]/50 flex items-center justify-center text-[13px]" style={{ animationDelay: `${0.4 + i * 0.1}s` }}>🎮</span>
          ))}
        </div>
      </div>

      {/* UFL in-game username — auto-shared into the room chat when it locks */}
      <div className="mt-5 max-w-xs mx-auto text-left">
        <label className="text-[10px] font-mono uppercase tracking-widest text-white/50">{t("ufl.qp.idle.uflName")}</label>
        <div className="relative mt-1">
          <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#10B981]/70" />
          <input
            type="text"
            value={uflName}
            onChange={(e) => setUflName(e.target.value)}
            maxLength={40}
            placeholder={t("ufl.qp.idle.uflNamePh")}
            data-testid="v2-ufl-qp-username"
            style={{ fontSize: "16px" }}
            className="w-full bg-black/40 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-[13px] text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]/60 focus:ring-1 focus:ring-[#10B981]/30 transition-all"
          />
        </div>
      </div>

      {qsize > 0 && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#60A5FA]" data-testid="v2-ufl-qp-queueline">
          <span className="relative flex w-2 h-2">
            <span className="ufl-sonar absolute inline-flex h-full w-full rounded-full bg-[#60A5FA]/60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#60A5FA]" />
          </span>
          {t("ufl.qp.idle.queueLine", { n: qsize, label: qsize === 1 ? t("ufl.qp.idle.queuePlayer") : t("ufl.qp.idle.queuePlayers") })}
        </div>
      )}
      <button
        type="button"
        onClick={() => onJoin(uflName)}
        disabled={busy}
        data-testid="v2-ufl-qp-join-btn"
        className="group relative mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#10B981] hover:bg-[#34D399] text-[#020617] font-extrabold px-5 py-3 text-sm uppercase tracking-wider disabled:opacity-50 overflow-hidden shadow-[0_8px_24px_-6px_rgba(16,185,129,0.7)] active:scale-[0.98] transition-transform"
      >
        {!busy && <span className="ufl-shimmer absolute inset-y-0 w-1/3 bg-white/30 blur-md skew-x-12" />}
        {busy ? <Loader2 className="relative w-4 h-4 animate-spin" /> : <Zap className="relative w-4 h-4" />}
        <span className="relative">{t("ufl.qp.idle.joinBtn")}</span>
      </button>
    </div>
  );
}

function QueuingCard({ t, state, onLeave, busy }) {
  const qsize = state.queue_size || 0;
  // Estimated wait: rough heuristic. If 1 in queue, ~30s; if 2-3, ~10s; full → instant.
  let eta = 30;
  if (qsize >= 4) eta = 5;
  else if (qsize >= 2) eta = 10;
  return (
    <div className="ufl-pop rounded-3xl bg-gradient-to-br from-amber-500/10 to-[#0F172A] border border-amber-500/40 p-6 text-center" data-testid="v2-ufl-qp-queuing">
      {/* Radar */}
      <div className="relative mx-auto mb-3 w-20 h-20">
        <span className="absolute inset-0 rounded-full border border-amber-400/30" />
        <span className="absolute inset-2 rounded-full border border-amber-400/20" />
        <span className="absolute inset-4 rounded-full border border-amber-400/15" />
        <span className="ufl-sonar absolute inset-0 rounded-full border-2 border-amber-400/40" />
        <div className="ufl-radar absolute inset-0">
          <span className="absolute top-1/2 left-1/2 w-1/2 h-[2px] bg-gradient-to-r from-amber-400 to-transparent origin-left" />
        </div>
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.9)]" />
      </div>
      <h2 className="font-heading text-xl uppercase tracking-wider">{t("ufl.qp.queuing.title")}</h2>
      <p className="text-[12px] text-white/70 mt-2 max-w-sm mx-auto">{t("ufl.qp.queuing.body")}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 max-w-xs mx-auto">
        <div className="rounded-xl bg-black/40 border border-white/10 p-2">
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{t("ufl.qp.queuing.position").replace(/\{.*?\}/g, "").trim()}</div>
          <div className="text-lg font-extrabold tabular-nums">{qsize}</div>
        </div>
        <div className="rounded-xl bg-black/40 border border-white/10 p-2">
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">ETA</div>
          <div className="text-lg font-extrabold tabular-nums">~{eta}s</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onLeave}
        disabled={busy}
        data-testid="v2-ufl-qp-leave-btn"
        className="mt-5 inline-flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-white/20 hover:bg-white/10 text-white/80 text-[11px] font-mono uppercase tracking-widest px-4 py-2"
      >
        <X className="w-3.5 h-3.5" /> {t("ufl.qp.queuing.leaveBtn")}
      </button>
    </div>
  );
}

function RoomView({ t, room, userId, onReload }) {
  const [leaving, setLeaving] = useState(false);
  const leaveRoom = async () => {
    const msg = room.status === "locked"
      ? "Leave this room? If the others aren't responding you can bail out — you won't be rated."
      : "Leave this room?";
    if (!window.confirm(msg)) return;
    setLeaving(true);
    try {
      await api.post(`/ufl/rooms/${room.id}/leave`);
      toast(t("ufl.qp.toast.leftRoom"));
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLeaving(false); }
  };
  return (
    <div className="space-y-3" data-testid={`v2-ufl-qp-room-${room.id}`}>
      <RoomHeader t={t} room={room} />
      <MembersGrid t={t} room={room} userId={userId} />
      {room.status === "locked" && <ChatBox t={t} roomId={room.id} userId={userId} />}
      {(room.status === "locked" || room.status === "closed") && (
        <RatingPanel t={t} room={room} userId={userId} onReload={onReload} />
      )}
      {/* Leave/exit — available while waiting OR during the 10-min locked window */}
      {room.status !== "closed" && (
        <button
          type="button"
          onClick={leaveRoom}
          disabled={leaving}
          data-testid="v2-ufl-qp-leave-room-btn"
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-300 text-[11px] font-mono uppercase tracking-widest px-4 py-2.5 disabled:opacity-50"
        >
          {leaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
          {room.status === "locked" ? t("ufl.qp.exitRoom") : t("ufl.qp.matched.leaveBtn")}
        </button>
      )}
    </div>
  );
}

function RoomHeader({ t, room }) {
  const status = room.status;
  if (status === "waiting") {
    const left = room.countdown_secs_left;
    const urgent = left != null && left <= 10;
    return (
      <div className="ufl-pop ufl-live rounded-2xl bg-amber-500/10 border border-amber-500/40 p-4 flex items-center gap-3" data-testid="v2-ufl-qp-room-header-waiting">
        <Clock className={`w-6 h-6 text-amber-400 ${urgent ? "ufl-urgent" : "animate-pulse"}`} />
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300">{t("ufl.qp.matched.title")}</div>
          <div className="text-sm text-white/90">{t("ufl.qp.matched.body", { n: room.members.length, cap: room.capacity })}</div>
        </div>
        {left != null && (
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-widest text-amber-300">timer</div>
            <div className={`text-xl font-extrabold tabular-nums ${urgent ? "ufl-urgent text-rose-300" : "text-amber-300"}`} data-testid="v2-ufl-qp-countdown">{fmt_secs(left)}</div>
          </div>
        )}
      </div>
    );
  }
  if (status === "locked") {
    const left = room.chat_secs_left;
    const urgent = left != null && left <= 30;
    return (
      <div className="ufl-pop ufl-live rounded-2xl bg-[#10B981]/10 border border-[#10B981]/40 p-4 flex items-center gap-3" data-testid="v2-ufl-qp-room-header-locked">
        <span className="relative flex w-6 h-6 items-center justify-center">
          <span className="ufl-sonar absolute inset-0 rounded-full bg-[#10B981]/40" />
          <Check className="relative w-6 h-6 text-[#10B981]" />
        </span>
        <div className="flex-1">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#10B981]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] ufl-urgent" /> {t("ufl.qp.locked.title")}
          </div>
          <div className="text-sm text-white/90">{t("ufl.qp.locked.body", { n: room.members.length, mins: 10 })}</div>
        </div>
        {left != null && (
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-widest text-[#10B981]">closes in</div>
            <div className={`text-xl font-extrabold tabular-nums ${urgent ? "ufl-urgent text-rose-300" : "text-[#10B981]"}`} data-testid="v2-ufl-qp-chatleft">{fmt_secs(left)}</div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white/5 border border-white/20 p-4 flex items-center gap-3" data-testid="v2-ufl-qp-room-header-closed">
      <X className="w-6 h-6 text-white/60" />
      <div className="flex-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">{t("ufl.qp.closed.title")}</div>
        <div className="text-sm text-white/80">{t("ufl.qp.closed.body")}</div>
      </div>
      {room.token_awarded && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFD700] text-[#020617] text-[10px] font-extrabold uppercase tracking-widest" data-testid="v2-ufl-qp-token-awarded">
          {t("ufl.qp.tokenEarned")}
        </span>
      )}
    </div>
  );
}

function MembersGrid({ t, room, userId }) {
  const full = room.members.length >= room.capacity;
  return (
    <div className="relative grid grid-cols-2 gap-2" data-testid="v2-ufl-qp-members">
      {room.members.map((m, idx) => (
        <div
          key={m.user_id}
          data-testid={`v2-ufl-qp-member-${m.user_id}`}
          style={{ animationDelay: `${idx * 0.08}s` }}
          className={`ufl-pop rounded-2xl bg-[#0F172A] border p-3 flex items-center gap-2 transition-transform hover:-translate-y-0.5 ${m.user_id === userId ? "border-[#FFD700]/60 ring-1 ring-[#FFD700]/30" : "border-white/10"}`}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${m.user_id === userId ? "bg-[#FFD700]/15" : "bg-[#1E3A8A]/40"}`}>
            {m.avatar_emoji || "⚽"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold truncate">@{m.username}{m.user_id === userId && <span className="ml-1 text-[8px] font-mono text-[#FFD700]">{t("ufl.qp.rate.you")}</span>}</div>
            {m.ufl_username ? (
              <div className="text-[10px] font-mono text-[#10B981] truncate" title="UFL username">UFL: {m.ufl_username}</div>
            ) : (
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
                {m.credibility?.score != null ? `★ ${m.credibility.score.toFixed(1)} · ${m.credibility.count}` : "no ratings"}
              </div>
            )}
          </div>
        </div>
      ))}
      {Array.from({ length: Math.max(0, room.capacity - room.members.length) }).map((_, i) => (
        <div key={`empty-${i}`} className="rounded-2xl bg-white/5 border border-dashed border-white/20 p-3 flex items-center gap-2 opacity-60" data-testid={`v2-ufl-qp-empty-${i}`}>
          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-xl text-white/30 animate-pulse">?</div>
          <div className="flex-1 text-[10px] font-mono uppercase tracking-widest text-white/30">waiting…</div>
        </div>
      ))}
      {full && (
        <span className="ufl-vs absolute top-1/2 left-1/2 z-10 w-9 h-9 rounded-full bg-[#020617] border border-white/20 flex items-center justify-center text-[10px] font-heading font-extrabold tracking-wider text-white/80 shadow-lg" aria-hidden="true">
          VS
        </span>
      )}
    </div>
  );
}

function ChatBox({ t, roomId, userId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const boxRef = useRef(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/ufl/rooms/${roomId}/chat`);
      setMsgs(data.messages || []);
    } catch (e) { /* noop */ }
  }, [roomId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs.length]);
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/ufl/rooms/${roomId}/chat`, { text: text.trim() });
      setText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };
  return (
    <div className="ufl-pop rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="v2-ufl-qp-chat">
      <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] ufl-urgent" /> Match chat
      </div>
      <div ref={boxRef} className="h-56 overflow-y-auto p-3 space-y-1.5 bg-black/30">
        {msgs.length === 0 ? (
          <div className="text-center text-xs text-white/30 py-8">{t("ufl.qp.chat.empty")}</div>
        ) : msgs.map((m) => {
          const sys = m.user_id === "system" || m.system;
          const mine = m.user_id === userId;
          if (sys) {
            return (
              <div key={m.id} className="ufl-slide-in flex justify-center">
                <div className="max-w-[92%] rounded-xl px-3 py-1.5 text-[11px] bg-[#10B981]/15 border border-[#10B981]/30 text-[#A7F3D0] whitespace-pre-line text-center font-mono shadow-[0_0_18px_-6px_rgba(16,185,129,0.7)]">
                  {m.text}
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className={`ufl-slide-in flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-xl px-3 py-1.5 text-[12px] whitespace-pre-line ${mine ? "bg-[#60A5FA] text-[#020617]" : "bg-white/10"}`}>
                {!mine && <div className="text-[9px] opacity-70 font-mono mb-0.5">@{m.username}</div>}
                {m.text}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 p-2 border-t border-white/10">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={t("ufl.qp.chat.placeholder")}
          data-testid="v2-ufl-qp-chat-input"
          maxLength={500}
          style={{ fontSize: "16px" }}
          className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#60A5FA]"
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !text.trim()}
          data-testid="v2-ufl-qp-chat-send"
          className="rounded-lg bg-[#60A5FA] text-[#020617] font-bold p-2 disabled:opacity-50"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function RatingPanel({ t, room, userId, onReload }) {
  const others = room.members.filter((m) => m.user_id !== userId);
  const myRatings = room.my_ratings || {};
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="v2-ufl-qp-rating">
      <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-white/10">
        {t("ufl.qp.rate.title")}
      </div>
      <div className="px-3 py-2 text-[10px] text-white/60">{t("ufl.qp.rate.note")}</div>
      <div className="divide-y divide-white/5">
        {others.map((m) => (
          <RateRow key={m.user_id} t={t} m={m} roomId={room.id} current={myRatings[m.user_id]} onSaved={onReload} />
        ))}
      </div>
    </div>
  );
}

function RateRow({ t, m, roomId, current, onSaved }) {
  const [busy, setBusy] = useState(false);
  const submit = async (rating) => {
    setBusy(true);
    try {
      await api.post(`/ufl/rooms/${roomId}/rate`, { ratee_id: m.user_id, rating });
      toast.success(t("ufl.qp.toast.rated"));
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 px-3 py-2" data-testid={`v2-ufl-qp-rate-row-${m.user_id}`}>
      <div className="w-8 h-8 rounded-full bg-[#1E3A8A]/40 flex items-center justify-center text-base">{m.avatar_emoji || "⚽"}</div>
      <div className="flex-1 min-w-0 text-xs font-bold truncate">@{m.username}</div>
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={busy}
            onClick={() => submit(n)}
            data-testid={`v2-ufl-qp-star-${m.user_id}-${n}`}
            className="p-0.5 disabled:opacity-50 transition-transform hover:scale-125 active:scale-90"
            aria-label={`Rate ${n}`}
          >
            <Star className={`w-4 h-4 transition-colors ${current && n <= current ? "fill-[#FFD700] text-[#FFD700]" : "text-white/30"}`} />
          </button>
        ))}
      </div>
      {current && <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-300 ml-1">{t("ufl.qp.rate.done")}</span>}
    </div>
  );
}
